import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

// Lazy-loaded SMS module components.
const SmsInbox = React.lazy(() => import("./SmsInbox"));
const SendSms = React.lazy(() => import("./SendSms"));
const NotificationRules = React.lazy(() => import("./NotificationRules"));
const SmsSettings = React.lazy(() => import("./SmsSettings"));
const SmsCosts = React.lazy(() => import("./SmsCosts"));

/**
 * SMS Module Config
 *
 * SINGLE SOURCE OF TRUTH for permissions is the backend
 * (business-builder-backend/src/modules/sms/permissions.ts → moduleId "sms").
 * The `id` here MUST equal that moduleId or the navbar will silently hide the module.
 *
 * Two-way SMS via Cellcast: a per-contact conversation inbox, compose + bulk send to
 * CRM contacts, inbound-SMS notification rules, and read-only sender settings.
 * Usage is metered per outbound segment (5c) and billed in the master SMS Usage page.
 */
const smsModuleConfig: ModuleConfig = {
  // MODULE IDENTITY — id matches backend moduleId "sms"
  id: "sms",
  name: "SMS",
  description: "Two-way SMS via Cellcast — inbox, send, staff broadcasts, and inbound alerts",
  version: "1.4.0",
  enabled: true,
  category: "communication",
  order: 11,
  icon: "💬",
  color: getColor("primaryBlue"),

  // ROUTES — permissionLevel resolved from backend at runtime
  routes: [
    {
      path: "/sms",
      component: SmsInbox,
      permissionLevel: "view",
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: "/sms/send",
      component: SendSms,
      permissionLevel: "create",
      useLayout: true,
    },
    {
      path: "/sms/notification-rules",
      component: NotificationRules,
      permissionLevel: "edit",
      useLayout: true,
    },
    {
      path: "/sms/settings",
      component: SmsSettings,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/sms/costs",
      component: SmsCosts,
      permissionLevel: "view",
      useLayout: true,
    },
  ],

  // NAVIGATION — permissionLevel resolved from backend at runtime
  navigation: [
    {
      label: "Inbox",
      path: "/sms",
      icon: "💬",
      permissionLevel: "view",
    },
    {
      label: "Send SMS",
      path: "/sms/send",
      icon: "✍️",
      permissionLevel: "create",
    },
    {
      label: "Reply alerts",
      path: "/sms/notification-rules",
      icon: "🔔",
      permissionLevel: "edit",
    },
    {
      label: "Costs",
      path: "/sms/costs",
      icon: "📊",
      permissionLevel: "view",
    },
    {
      label: "Settings",
      path: "/sms/settings",
      icon: "⚙️",
      permissionLevel: "view",
    },
  ],

  // Permissions come from the backend (single source of truth).
  // See: business-builder-backend/src/modules/sms/permissions.ts
  // These empty arrays get replaced at runtime by ModulePermissionsContext.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: "Send SMS",
      path: "/sms/send",
      icon: "✍️",
      description: "Send an SMS to a contact",
    },
  ],

  // 📝 CHANGELOG
  changelog: [
    {
      version: "1.4.0",
      releaseDate: "2026-10-18",
      title: "Easier to read and use, on every SMS page",
      features: [
        "🎨 Every SMS page has the new look: bigger text, bigger buttons with words, plain-English labels",
        "📱 On a phone the inbox shows the list or one conversation, with a button back to the list",
        "🔔 Notification Rules are now Reply alerts — turning one on or off shows at once, and deleting asks first",
        "✍️ Sender-name registration is edited where you read it, one section at a time, and sending it to Cellcast asks first",
        "🕐 Costs use Brisbane days and months, and say when a list is cut short",
        "🔒 Asking for a number and the registration only show to people who are allowed to change them",
      ],
    },
    {
      version: "1.3.0",
      releaseDate: "2026-10-17",
      title: "Replies reach the right site",
      features: [
        "✅ A reply goes to the site whose number it was sent to; on a shared number, to the site that texted that person last",
        "✅ Replies no site can be proven to own are held for a platform admin (shown at the top of the inbox) — never dropped into another site's inbox",
        "✅ Until now every reply landed in the Tom Miller Services inbox",
      ],
    },
    {
      version: "1.2.0",
      releaseDate: "2026-10-16",
      title: "Your number in the inbox + readability",
      features: [
        "✅ The inbox shows this site's own SMS number (or says there isn't one, with a link to request one)",
        "✅ Nothing under 14px; rule buttons are worded (Test / Edit / Delete) and 44px tall",
        "✅ Installed on One Group (its own Cellcast number + key)",
      ],
    },
    {
      version: "1.1.0",
      releaseDate: "2026-07-23",
      title: "Staff broadcasts",
      features: [
        "✅ Broadcast to staff by department, role or status — 'Who gets this?' ticked list",
        "✅ Work-number vs personal-number targeting (work falls back when none on file)",
        "✅ Staff replies matched by work number and named in the inbox",
        "✅ Send mutations permission-gated; audience resolved server-side",
      ],
    },
    {
      version: "1.0.0",
      releaseDate: "2026-06-16",
      title: "Two-way SMS module",
      features: [
        "✅ Conversation inbox threaded per contact",
        "✅ Compose single + bulk SMS to CRM contacts",
        "✅ Inbound-SMS notification rules (email/SMS staff when a customer texts back)",
        "✅ Per-segment usage metering for billing (5c/segment outbound)",
      ],
      notes: "Initial release — Cellcast-backed, tenant-isolated by API key.",
    },
  ],

  roadmap: [],
};

export default smsModuleConfig;
